import { TranslateResult } from 'vue-i18n';

import axios, { AxiosError, AxiosResponse } from 'axios';

import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import { PublicKeyCredentialCreationOptionsJSON } from '@simplewebauthn/typescript-types';
import { IP_RESTRICTED_AUTH_DENIED_REASON } from 'boot/axiosAuthDeniedReason';
import { globalState, simpleObservableStore } from 'boot/simpleObservableStore';

import { notifyService } from 'src/services/notify/NotifyService';

import useAuthentication from 'src/filters/authentication';

import { splitHostname, urlForInstance } from 'src/util/common';
import { EventBus } from 'src/util/eventBus/EventBus';
import { isIos, isSafari } from 'src/util/platform';
import { clearUserScopedServiceWorkerCaches } from 'src/util/pwa/userScopedServiceWorkerCaches';

export const userService = {
	endLocalSession,
	login,
	logout,
	registerWebauthnDevice,
	validateUsernameAndPasswordLogin,
	validateWebauthnLogin,
	webauthnLogin,
};

type AuthResponse = {
	accessToken: string;
	authToken: string;
	success: boolean;
};

type NormalizedAuthResponse = {
	csrfToken?: string;
	persistAccessToken: boolean;
} & AuthResponse;

type UsernameAndPasswordLoginPayload = {
	instance: string;
	password: string;
	twoFactorAuthenticationCode: string;
	username: string;
};

const appUsernameAndPasswordLoginPath = '/auth/login';

const loginErrorStatus = (reason: unknown): number | undefined => (reason as AxiosError).response?.status;

// POST /auth/login and /auth/verify-authentication answer 403 with the body ip_restricted when the user's
// ZF login IP restriction does not allow the client address; any other 403 means "not unlocked for the App".
const loginErrorIsIpRestricted = (reason: unknown): boolean =>
	loginErrorStatus(reason) === 403 && (reason as AxiosError).response?.data === IP_RESTRICTED_AUTH_DENIED_REASON;

const notifyForbiddenLogin = (t: <Key extends string>(key: Key) => TranslateResult, reason: unknown): void => {
	if (loginErrorIsIpRestricted(reason)) {
		notifyService.error(String(t('login_ip_restricted')));
	} else {
		notifyService.warn(String(t('app_not_unlocked')));
	}
};

const postUsernameAndPasswordLogin = async (
	url: string,
	payload: UsernameAndPasswordLoginPayload
): Promise<NormalizedAuthResponse> => {
	const { data } = await axios.post<AuthResponse>(`${url}${appUsernameAndPasswordLoginPath}`, payload);

	return {
		accessToken: data.accessToken,
		authToken: data.authToken,
		persistAccessToken: false,
		success: data.success,
	};
};

const tokenForLoginAuthResponse = (authResponse: NormalizedAuthResponse): string =>
	authResponse.persistAccessToken || isIos() || isSafari() ? authResponse.accessToken : '';

const tokenForValidationAuthResponse = (authResponse: NormalizedAuthResponse): string =>
	authResponse.persistAccessToken || isIos() ? authResponse.accessToken : '';

const notifyLoginErrorAndThrow = (
	t: <Key extends string>(key: Key) => TranslateResult,
	twoFactorAuthenticationCode: string,
	reason: unknown
): never => {
	if (loginErrorStatus(reason) === 403) {
		notifyForbiddenLogin(t, reason);
	} else if (loginErrorStatus(reason) === 404) {
		notifyService.error(
			String(twoFactorAuthenticationCode !== '' ? t('app_login_failed_with_token') : t('app_login_failed'))
		);
	}

	throw reason;
};

type GenerateRegistrationOptionsResponse = {
	authToken: string;
	options: PublicKeyCredentialCreationOptionsJSON;
};

type GenerateAuthenticationOptionsResponse = {
	authToken: string;
	options: PublicKeyCredentialCreationOptionsJSON;
};

type VerifyResponse = {
	accessToken: string;
	username: string;
	verified: boolean;
};

async function login(
	t: <Key extends string>(key: Key) => TranslateResult,
	instance: string,
	username: string,
	password: string,
	twoFactorAuthenticationCode: string,
	eventBus: EventBus
) {
	globalState.authToken = '';
	const url = await urlForInstance(instance.toLowerCase());
	let data: NormalizedAuthResponse;
	try {
		data = await postUsernameAndPasswordLogin(url, {
			instance: instance,
			password: password,
			twoFactorAuthenticationCode: twoFactorAuthenticationCode,
			username: username.toLowerCase(),
		});

		if (data.success) {
			const { addInstanceAuthenticationConfigurationAndActivate } = useAuthentication();
			const hostnameParts = splitHostname(url);
			globalState.authToken = data.authToken;
			globalState.csrfToken = data.csrfToken ?? globalState.csrfToken;
			addInstanceAuthenticationConfigurationAndActivate({
				name: hostnameParts?.subdomain ?? '',
				token: tokenForLoginAuthResponse(data),
				url: url,
				user: username,
			});
			globalState.authToken = '';
		} else {
			await logout(instance, username, eventBus);
		}
	} catch (reason: unknown) {
		notifyLoginErrorAndThrow(t, twoFactorAuthenticationCode, reason);
	}
}

async function registerWebauthnDevice(
	t: <Key extends string>(key: Key) => TranslateResult,
	instance: string,
	username: string,
	password: string,
	twoFactorAuthenticationCode: string
) {
	const url = await urlForInstance(instance.toLowerCase());
	let registrationSuccessful = false;
	const { data } = await axios
		.post<AuthResponse>(`${url}/auth/login`, {
			instance: instance,
			password: password,
			twoFactorAuthenticationCode: twoFactorAuthenticationCode,
			username: username.toLowerCase(),
		})
		.catch((reason: unknown) => {
			// Like the login paths; the authentication store still reports the failed registration afterwards.
			if (loginErrorIsIpRestricted(reason)) {
				notifyService.error(String(t('login_ip_restricted')));
			}
			throw reason;
		});
	if (data.success === true) {
		let attResp;
		globalState.authToken = data.authToken;
		await axios
			.get<GenerateRegistrationOptionsResponse>(`${url}/auth/generate-registration-options`)
			.then(async (opts) => {
				globalState.authToken = opts.data.authToken;
				attResp = await startRegistration(opts.data.options);
				const verificationResp = await axios.post<VerifyResponse>(`${url}/auth/verify-registration`, attResp);
				registrationSuccessful = verificationResp.data.verified;
			});
	}

	return registrationSuccessful;
}

async function webauthnLogin(t: <Key extends string>(key: Key) => TranslateResult, instance: string) {
	globalState.authToken = '';
	const url = await urlForInstance(instance.toLowerCase());
	const opts = await axios.get<GenerateAuthenticationOptionsResponse>(`${url}/auth/generate-authentication-options`, {
		params: {
			instance: instance,
		},
	});

	globalState.authToken = opts.data.authToken;

	const asseResp = await startAuthentication(opts.data.options);

	let verificationResp: VerifyResponse;

	await axios
		.post<VerifyResponse>(`${url}/auth/verify-authentication`, {
			asseResp: asseResp,
			instance: instance,
		})
		.then((response: AxiosResponse<VerifyResponse>) => {
			verificationResp = response.data;
			if (verificationResp !== null && verificationResp.verified) {
				const { addInstanceAuthenticationConfigurationAndActivate } = useAuthentication();
				const hostnameParts = splitHostname(url);

				addInstanceAuthenticationConfigurationAndActivate({
					name: hostnameParts?.subdomain ?? '',
					token: isIos() ? verificationResp.accessToken : '',
					url: url,
					user: verificationResp.username,
				});
				globalState.authToken = '';
			}

			return verificationResp && verificationResp.verified;
		})
		.catch((reason: AxiosError) => {
			if (reason.response?.status === 403) {
				notifyForbiddenLogin(t, reason);
				throw reason;
			}
		});
}

async function validateUsernameAndPasswordLogin(
	t: <Key extends string>(key: Key) => TranslateResult,
	instance: string,
	username: string,
	password: string,
	twoFactorAuthenticationCode: string
): Promise<{
	success: boolean;
	token: string;
}> {
	globalState.authToken = '';
	const url = await urlForInstance(instance.toLowerCase());
	let data: NormalizedAuthResponse;
	let returnValue = {
		success: false,
		token: '',
	};

	try {
		data = await postUsernameAndPasswordLogin(url, {
			instance: instance,
			password: password,
			twoFactorAuthenticationCode: twoFactorAuthenticationCode,
			username: username.toLowerCase(),
		});

		returnValue = {
			success: data.success,
			token: tokenForValidationAuthResponse(data),
		};
	} catch (reason: unknown) {
		notifyLoginErrorAndThrow(t, twoFactorAuthenticationCode, reason);
	}

	return returnValue;
}

async function validateWebauthnLogin(
	t: <Key extends string>(key: Key) => TranslateResult,
	instance: string
): Promise<{
	success: boolean;
	token: string;
} | void> {
	globalState.authToken = '';
	const url = await urlForInstance(instance.toLowerCase());
	const opts = await axios.get<GenerateAuthenticationOptionsResponse>(`${url}/auth/generate-authentication-options`, {
		params: {
			instance: instance,
		},
	});
	globalState.authToken = opts.data.authToken;

	const asseResp = await startAuthentication(opts.data.options);
	let verificationResp: VerifyResponse;

	return await axios
		.post<VerifyResponse>(`${url}/auth/verify-authentication`, {
			asseResp: asseResp,
			instance: instance,
		})
		.then((response: AxiosResponse<VerifyResponse>) => {
			verificationResp = response.data;
			return {
				success: verificationResp && verificationResp.verified,
				token: isIos() ? verificationResp.accessToken : '',
			};
		})
		.catch((reason: AxiosError) => {
			if (reason.response?.status === 403) {
				notifyForbiddenLogin(t, reason);
				throw reason;
			}
		});
}

async function logout(instance: string, username: string, eventBus: EventBus) {
	if (instance.length > 0) {
		eventBus.emit('logout');
		const url = await urlForInstance(instance.toLowerCase());
		await axios.post(`${url}/auth/logout`, {
			instance: instance.toLowerCase(),
			username: username.toLowerCase(),
		});
	}

	await clearLocalAuthentication(instance);
}

// Ends the App session on this device only, without POST /auth/logout: used after ifs_zf revoked the session for
// ip_restricted, whose stored reason a server-side logout would overwrite with 'logout' (boot/axiosAuthDeniedReason).
async function endLocalSession(instance: string, eventBus: EventBus) {
	if (instance.length > 0) {
		eventBus.emit('logout');
	}

	await clearLocalAuthentication(instance);
}

async function clearLocalAuthentication(instance: string) {
	const { logoutFromActiveSession } = useAuthentication();

	if (instance.length > 0) {
		globalState.authToken = '';
		document.cookie = '__Host-accessToken=; path=/; max-age=0; SameSite=Strict; Secure';
	}

	await clearUserScopedServiceWorkerCaches().catch((error) => {
		console.warn('Failed to clear user-scoped service worker caches on logout:', error);
	});

	if (instance.length > 0 && simpleObservableStore.registration?.waiting) {
		simpleObservableStore.registration.waiting.postMessage({
			clientHandlesControllerChange: true,
			type: 'SKIP_WAITING',
		});
	}

	logoutFromActiveSession();
}
